// 260722 copilot E4-P2 — mô phỏng op CẤU TRÚC trên bản NHÁP (client).
//
// Vì sao cần: tách/gộp/chèn/xoá đổi TẬP event, không biểu diễn được bằng OpsMap keyed-theo-id.
// Nháp phải tự dựng kết quả để user THẤY trước khi Lưu; nguồn chân lý khi Lưu vẫn là hàm server
// (filmEventRepository + filmEventSplitMerge, E4-P1). Preview có thể lệch nhẹ ở ca hiếm — sau khi
// Lưu reload là thấy bản thật.
//
// Pure: không gọi API, không đụng state React → unit test được.
import type { EventPlan, FilmEvent } from '../../types/film';
// 260923 E1-P1 — luật event dạng chuỗi nhịp (cùng luật BE): gộp nối nhịp + dời neo, hook đứng riêng, tách v2 bị chặn.
import {
  SPLIT_BEAT_REJECT_V1, SPLIT_REJECT_V2, actionOfBeats, canMergeV2, emptyBeatMetaV2, isBeatMetaV2, mergeBeatMetaV2, planSplitByBeat,
  shiftAnchor, validSplitPoints,
} from './beatSequenceUtils';

export type StructOp =
  // 260923 E1-P2 (R8): event v2 tách theo NHỊP (`keepBeatCount`), event v1 theo shot (`keepShotCount`).
  | { type: 'splitEvent'; eventId: string; keepShotCount?: number; keepBeatCount?: number; tempId: string }
  | { type: 'mergeNext'; eventId: string }
  | { type: 'insertAfter'; eventId: string; tempId: string }
  | { type: 'deleteEvent'; eventId: string };

/** OpsMap của EventPlanTab (chỉ phần cần cho prune) — tránh import vòng. */
export interface OpsMapLite {
  ev: Record<string, unknown>;
  prompt: Record<string, unknown>;
  dlg: Record<string, unknown>;
  sh: Record<string, unknown>;
  struct: StructOp[];
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/** Đánh số lại eventOrder 1..N theo thứ tự hiển thị (mirror renumberPlan của server). */
const renumber = (plan: EventPlan): EventPlan => {
  let n = 0;
  plan.scenes.forEach((sc) => sc.events.forEach((e) => { e.eventOrder = ++n; }));
  return plan;
};

/** Sau tách/gộp: duration + ledger + prompt cũ KHÔNG còn đúng (server xoá — nháp hiển thị y vậy). */
const clearDerived = (e: FilmEvent) => {
  e.genDurationS = null;
  e.continuityLedger = null;
  e.authorPrompt = null;
  e.authorStatus = null;
  // 261010 P1a (mirror `clearSkyFieldsIfSkyPlan` của server): ranh giới đổi ⇒ đề xuất engine + giai đoạn cũ bỏ. Chỉ đụng khi cột CÓ mặt
  // (event plan thường không có ⇒ nháp y nguyên như trước đợt này).
  const sky = e as unknown as Record<string, unknown>;
  for (const k of ['videoEngine', 'engineSource', 'engineReason', 'videoMeta']) if (k in sky) sky[k] = null;
};

const locate = (plan: EventPlan, eventId: string) => {
  for (const sc of plan.scenes) {
    const i = sc.events.findIndex((e) => e.id === eventId);
    if (i >= 0) return { scene: sc, index: i, event: sc.events[i] };
  }
  return null;
};

/**
 * Áp MỘT op cấu trúc lên draft, trả draft MỚI (không mutate input).
 * Op trỏ event không tồn tại / không hợp lệ (gộp ở cuối scene) → trả draft nguyên vẹn (nút đã ẩn ở
 * UI; đây là lưới an toàn, không throw giữa lúc user đang gõ).
 */
export const applyStructToDraft = (draft: EventPlan, op: StructOp): EventPlan => {
  // 260923: op trái luật event dạng chuỗi ⇒ từ chối TRƯỚC khi đổi nháp (UI đọc lý do qua structRejectReason).
  if (structRejectReason(draft, op)) return draft;
  const plan = clone(draft);
  const at = locate(plan, op.eventId);
  if (!at) return draft;
  const { scene, index, event } = at;

  if (op.type === 'deleteEvent') {
    scene.events.splice(index, 1);
    return renumber(plan);
  }

  if (op.type === 'insertAfter') {
    const fresh: FilmEvent = {
      id: op.tempId, sceneId: scene.id, eventOrder: event.eventOrder + 1,
      kind: 'SINGLE_SHOT' as any, action: '', shots: [], dialogueLines: [],
      // 260923 (Q8/D9): chèn sau event dạng chuỗi ⇒ event mới v2 RỖNG (mirror server) — plan không bị lẫn v1/v2.
      ...(isBeatMetaV2(event.beatMeta) ? { beatMeta: emptyBeatMetaV2() } : {}),
    } as FilmEvent;
    scene.events.splice(index + 1, 0, fresh);
    return renumber(plan);
  }

  if (op.type === 'mergeNext') {
    const next = scene.events[index + 1];
    if (!next) return draft; // cuối scene — KHÔNG gộp xuyên scene (mirror server)
    const shotOffset = event.shots.length;
    const lineOffset = event.dialogueLines.length;
    next.shots.forEach((s, i) => {
      const moved: any = { ...s, orderIndex: shotOffset + i + 1 };
      // speaksDialogueIndex là 1-based THEO EVENT → dời theo số thoại đã có ở event trước.
      if (moved.presence?.speaksDialogueIndex != null) {
        moved.presence = { ...moved.presence, speaksDialogueIndex: moved.presence.speaksDialogueIndex + lineOffset };
      }
      event.shots.push(moved);
    });
    // 260923: v2+v2 ⇒ nối nhịp + dời neo câu của B theo số nhịp của A (mirror server). v1 ⇒ như cũ.
    const v2 = isBeatMetaV2(event.beatMeta) && isBeatMetaV2(next.beatMeta);
    const m = v2 ? mergeBeatMetaV2(event.beatMeta!, next.beatMeta!) : null;
    next.dialogueLines.forEach((l, i) => event.dialogueLines.push({
      ...l, orderIndex: lineOffset + i + 1,
      ...(m ? { afterActionBeatOrder: shiftAnchor(l.afterActionBeatOrder, m.anchorShift) } : {}),
    }));
    event.action = [event.action, next.action].filter(Boolean).join(' ');
    if (m) event.beatMeta = m.beatMeta;
    // D11: event v2 gộp là chuỗi nhiều nhịp ⇒ MULTI_SHOT nếu >1 nhịp HOẶC >1 shot.
    event.kind = ((m ? ((m.beatMeta.actionBeats || []).length > 1 || event.shots.length > 1) : event.shots.length > 1)
      ? 'MULTI_SHOT' : 'SINGLE_SHOT') as any;
    clearDerived(event);
    scene.events.splice(index + 1, 1);
    return renumber(plan);
  }

  // 260923 E1-P2 (R7): event dạng chuỗi tách THEO NHỊP — mirror `filmEventRepository.splitEventByBeat` (shot hai nửa bị
  // xoá, Phase 3 dàn lại; thời lượng/prompt/ledger xoá). structRejectReason đã chặn k sai / thiếu keepBeatCount.
  if (isBeatMetaV2(event.beatMeta) && op.keepBeatCount !== undefined) {
    const bm = event.beatMeta!;
    const p = planSplitByBeat(bm.actionBeats || [], bm.sourceLines || [], event.dialogueLines, op.keepBeatCount);
    const kindOf = (n: number) => (n > 1 ? 'MULTI_SHOT' : 'SINGLE_SHOT') as any;
    const fresh: FilmEvent = {
      id: op.tempId, sceneId: scene.id, eventOrder: event.eventOrder + 1,
      kind: kindOf(p.second.beats.length), action: actionOfBeats(p.second.beats),
      beatMeta: { ...bm, actionBeats: p.second.beats, sourceLines: p.second.sourceLines },
      shots: [], dialogueLines: p.second.lines,
    } as FilmEvent;
    event.beatMeta = { ...bm, actionBeats: p.first.beats, sourceLines: p.first.sourceLines };
    event.action = actionOfBeats(p.first.beats);
    event.kind = kindOf(p.first.beats.length);
    event.shots = [];
    event.dialogueLines = p.first.lines;
    clearDerived(event);
    clearDerived(fresh);
    scene.events.splice(index + 1, 0, fresh);
    return renumber(plan);
  }

  // splitEvent — thoại ĐI THEO shot đọc nó; câu MỒ CÔI ở lại event gốc (khớp filmEventSplitMerge).
  const keepCount = Math.max(0, Math.min(op.keepShotCount ?? 0, event.shots.length));
  const keepShots = event.shots.slice(0, keepCount);
  const movedShots = event.shots.slice(keepCount);
  if (!movedShots.length) return draft; // không có gì để tách

  const movedLineIdx = new Set<number>();
  movedShots.forEach((s: any) => {
    const idx = s.presence?.speaksDialogueIndex;
    if (idx != null) movedLineIdx.add(idx);
  });
  const keepLines = event.dialogueLines.filter((_, i) => !movedLineIdx.has(i + 1));
  const movedLines = event.dialogueLines.filter((_, i) => movedLineIdx.has(i + 1));

  // Remap speaksDialogueIndex sang thứ tự MỚI của từng bên (1-based theo event).
  const remap = (shots: any[], lines: any[]) => {
    const posOf = new Map<string, number>();
    lines.forEach((l, i) => posOf.set(l.id, i + 1));
    return shots.map((s, i) => {
      const oldIdx = s.presence?.speaksDialogueIndex;
      const oldLine = oldIdx != null ? event.dialogueLines[oldIdx - 1] : undefined;
      const newIdx = oldLine ? posOf.get(oldLine.id) ?? null : null;
      return { ...s, orderIndex: i + 1, ...(s.presence ? { presence: { ...s.presence, speaksDialogueIndex: newIdx } } : {}) };
    });
  };

  const fresh: FilmEvent = {
    id: op.tempId, sceneId: scene.id, eventOrder: event.eventOrder + 1,
    kind: (movedShots.length > 1 ? 'MULTI_SHOT' : 'SINGLE_SHOT') as any,
    action: event.action, // copy — người sửa lại cho đúng nửa sau (mirror server)
    beatMeta: event.beatMeta,
    shots: remap(movedShots, movedLines),
    dialogueLines: movedLines.map((l, i) => ({ ...l, orderIndex: i + 1 })),
  } as FilmEvent;

  event.shots = remap(keepShots, keepLines);
  event.dialogueLines = keepLines.map((l, i) => ({ ...l, orderIndex: i + 1 }));
  event.kind = (keepShots.length > 1 ? 'MULTI_SHOT' : 'SINGLE_SHOT') as any;
  clearDerived(event);
  clearDerived(fresh);
  scene.events.splice(index + 1, 0, fresh);
  return renumber(plan);
};

/**
 * 260923 E1-P1 — lý do một op cấu trúc bị TỪ CHỐI trên nháp (null = hợp lệ). Cùng luật server (ConflictError 409):
 * tách event dạng chuỗi ⇒ chặn (Phase 2 mới tách theo nhịp); gộp dính hook hoặc v1+v2 ⇒ chặn. Plan v1 ⇒ không chặn gì.
 */
export const structRejectReason = (draft: EventPlan, op: StructOp): string | null => {
  const at = locate(draft, op.eventId);
  if (!at) return null;
  if (op.type === 'splitEvent') {
    // 260923 E1-P2 (R8): v2 cần keepBeatCount thuộc ranh giới hợp lệ (R5); v1 không tách theo nhịp.
    if (isBeatMetaV2(at.event.beatMeta)) {
      if (op.keepBeatCount === undefined || op.keepBeatCount === null) return SPLIT_REJECT_V2;
      const ks = validSplitPoints(at.event.beatMeta!.actionBeats || [], at.event.beatMeta!.sourceLines || []);
      if (!ks.includes(op.keepBeatCount)) return `Không tách được sau nhịp ${op.keepBeatCount} — ranh giới hợp lệ: ${ks.length ? ks.join(', ') : 'không có'}`;
    } else if (op.keepBeatCount !== undefined && op.keepBeatCount !== null) {
      return SPLIT_BEAT_REJECT_V1;
    }
  }
  if (op.type === 'mergeNext') {
    if (!at.scene.events[at.index + 1]) return null; // cuối scene — nhánh cũ tự bỏ qua
    const si = draft.scenes.indexOf(at.scene);
    const v = canMergeV2(draft.scenes, { sceneIdx: si, eventIdx: at.index }, { sceneIdx: si, eventIdx: at.index + 1 });
    if (!v.ok) return v.reason || 'Không gộp được.';
  }
  return null;
};

/**
 * Dọn op FIELD trỏ vào event/shot không còn trong draft (bị xoá hoặc bị gộp mất).
 * Không dọn ⇒ server nhận op mồ côi → lỗi lúc bấm Lưu (E4-P1 chặn bằng resolveLive) = UX tệ.
 * Mutate `ops` tại chỗ (dùng trong mutate() của EventPlanTab).
 */
export const pruneOrphanFieldOps = (draft: EventPlan, ops: OpsMapLite): void => {
  const liveEvents = new Set<string>();
  const liveShots = new Set<string>();
  draft.scenes.forEach((sc) => sc.events.forEach((e) => {
    liveEvents.add(e.id);
    (e.shots || []).forEach((s) => liveShots.add(s.id));
  }));
  for (const key of Object.keys(ops.ev)) if (!liveEvents.has(key)) delete ops.ev[key];
  for (const key of Object.keys(ops.prompt)) if (!liveEvents.has(key)) delete ops.prompt[key];
  for (const key of Object.keys(ops.dlg)) if (!liveEvents.has(key)) delete ops.dlg[key];
  for (const key of Object.keys(ops.sh)) if (!liveShots.has(key)) delete ops.sh[key];
};
